import { useEffect, useState } from 'react';
import { logger } from '../logger';
import { deviceIdService } from '../services/deviceIdService';

/**
 * Hook for getting device ID
 * Provides reactive access to device ID with loading state
 */
export const useDeviceId = () => {
    const [deviceId, setDeviceId] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchDeviceId = async () => {
            try {
                setIsLoading(true);
                setError(null);
                const id = await deviceIdService.getDeviceId();
                setDeviceId(id);
            } catch (err) {
                const errorMessage = err instanceof Error ? err.message : 'Failed to get device ID';
                setError(errorMessage);
                logger.error('useDeviceId', 'Error fetching device ID:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDeviceId();
    }, []);

    const refreshDeviceId = async () => {
        try {
            setIsLoading(true);
            setError(null);
            const id = await deviceIdService.getDeviceId();
            setDeviceId(id);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to refresh device ID';
            setError(errorMessage);
            logger.error('useDeviceId', 'Error refreshing device ID:', err);
        } finally {
            setIsLoading(false);
        }
    };

    const clearDeviceId = async () => {
        try {
            await deviceIdService.clearDeviceId();
            setDeviceId(null);
            setError(null);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to clear device ID';
            setError(errorMessage);
            logger.error('useDeviceId', 'Error clearing device ID:', err);
        }
    };

    return {
        deviceId,
        isLoading,
        error,
        refreshDeviceId,
        clearDeviceId
    };
};

export default useDeviceId;
